import _ from "lodash";
import React, { useMemo } from "react";
import { connect } from "react-redux";
import { compose, graphql } from "lib/apolloClient";

import withLoader from "UIComponents/GqlWithLoader";
import {
  FILTER_NAME_GRADES,
  FILTER_NAME_SUBJECTS,
  FILTER_NAME_CONTENT_TYPE,
} from "Course/Constants/StringConstants";
import { useI18n } from "Hooks";
import { CURRICULUM_TYPE_MYP } from "Constants/stringConstants";

import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";

import {
  getLibraryGradesFromCache,
  getStaffGradesFromCache,
} from "ToddleLibrary/modules/ToddleLibraryGraphqlHelpers";
import {
  getLibraryGradesQuery,
  getStaffGradesQuery,
} from "ToddleLibrary/modules/ToddleLibraryQuery";
import {
  getDefaultGradeOfStaff,
  getSubjectOptionsFromGradeId,
  getGradeOptions,
  isMYPWorksheetEnabledForLibrary,
} from "ToddleLibrary/modules/ToddleLibraryUtils";
import { getIsWorksheetsEnabled } from "Worksheet/modules/WorksheetFeatureFlags";

const styles = {
  labelStyle: {
    maxWidth: "180px",
  },
  leLabelStyle: {
    maxWidth: "96px",
  },
};

const ToddleLibraryFiltersHOC = WrapperComponent => {
  const WithToddleLibraryFilters = props => {
    const {
      gradeList,
      organizationId,
      curriculumProgramType,
      activeEntityType,
    } = props;
    const { t } = useI18n();

    const contentTypeOptions = [
      {
        key: "all",
        value: "ALL",
        label: t("common:all_label", { label: t("common:post_plural") }),
      },
      {
        key: "saved",
        value: "SAVED",
        label: t("common:my_bookmarks"),
      },
    ];

    const defaultGrade = getDefaultGradeOfStaff({
      curriculumProgramType,
      activeEntityType,
    });

    const shouldShowWorksheetInMyp =
      curriculumProgramType === CURRICULUM_TYPE_MYP &&
      isMYPWorksheetEnabledForLibrary() &&
      getIsWorksheetsEnabled();

    const subjectsList = getSubjectOptionsFromGradeId(
      _.get(defaultGrade, "id"),
      "COMMUNITY",
      curriculumProgramType,
      activeEntityType
    );

    const gradeOptions = _.map(gradeList, grade => ({
      ...grade,
      value: grade.id,
      key: grade.id,
    }));

    const labelStyle = _.isEqual(activeEntityType, "learning_experience")
      ? styles.leLabelStyle
      : styles.labelStyle;

    const filtersInfo = useMemo(
      () => [
        {
          id: FILTER_NAME_CONTENT_TYPE,
          name: FILTER_NAME_CONTENT_TYPE,
          options: contentTypeOptions,
          type: "DROPDOWN",
          labelButtonType: "BUTTON_DISCLOSURE",
          variant: "plain",
        },
        {
          id: FILTER_NAME_GRADES,
          name: FILTER_NAME_GRADES,
          options: gradeOptions,
          label: "common:grade",
          isCheckList: false,
          type: "DROPDOWN",
          labelButtonType: "FILTER_LABEL",
          labelStyle: labelStyle,
        },
        {
          id: FILTER_NAME_SUBJECTS,
          name: FILTER_NAME_SUBJECTS,
          options: subjectsList,
          label: "common:subject",
          isCheckList: false,
          type: "DROPDOWN",
          labelButtonType: "FILTER_LABEL",
          labelStyle: labelStyle,
        },
      ],
      [contentTypeOptions, gradeOptions, subjectsList, curriculumProgramType]
    );

    let filters = filtersInfo;
    if (_.isEqual(activeEntityType, "worksheet") && !shouldShowWorksheetInMyp) {
      filters = _.filter(
        filters,
        filter => filter?.id !== FILTER_NAME_CONTENT_TYPE
      );
    }

    const newProps = {
      ...props,
      filtersInfo: filters,
      defaultSubject: _.get(subjectsList, [0, "key"]),
      defaultGrades: _.get(defaultGrade, "id"),
      defaultContentType: _.get(contentTypeOptions, [0, "value"]),
      allGrades: gradeOptions,
      subjectsList,
    };

    return <WrapperComponent {...newProps} />;
  };
  const mapStateToProps = (state, ownProps) => {
    const platformUserInfo = getUserInfo({ portalType: "PLANNER" });
    const platformOrgId = platformUserInfo.org_id;

    const libraryUserInfo = getUserInfo({ portalType: "COMMUNITY" });
    const libraryUserId = libraryUserInfo.id;

    const curriculumProgramType = state.platform.currentCurriculumProgram.type;

    return {
      curriculumProgramType,
      platformOrgId,
      libraryUserId,
      isData: true,
      isLoading: false,
    };
  };
  return compose(
    connect(mapStateToProps),
    graphql(getLibraryGradesQuery, {
      name: "getLibraryGrades",
      skip: ({ platformOrgId }) => !platformOrgId,
      options: ({ platformOrgId }) => ({
        variables: {
          id: platformOrgId,
          portalType: "PLANNER",
        },
        fetchPolicy: "cache-and-network",
      }),
      props: ({
        getLibraryGrades,
        ownProps: {
          isData,
          isLoading,
          platformOrgId,
          activeEntityType,
          curriculumProgramType,
        },
      }) => {
        const organizationGradeDetails = getLibraryGradesFromCache({
          id: platformOrgId,
        });

        const gradeList = getGradeOptions({
          curriculumProgramType,
          activeEntityType,
        });

        return {
          gradeList,
          isData: isData && !_.isEmpty(organizationGradeDetails),
          isLoading:
            isLoading || _.includes([1, 2], getLibraryGrades.networkStatus),
        };
      },
    }),
    graphql(getStaffGradesQuery, {
      name: "getStaffGrades",
      skip: ({ libraryUserId }) => !libraryUserId,
      options: ({ libraryUserId }) => ({
        variables: {
          id: libraryUserId,
          portalType: "COMMUNITY",
        },
        fetchPolicy: "cache-and-network",
      }),
      props: ({
        getStaffGrades,
        ownProps: { isData, isLoading, libraryUserId },
      }) => {
        const staffGradeDetails = getStaffGradesFromCache({
          id: libraryUserId,
        });

        return {
          isData: isData && !_.isEmpty(staffGradeDetails),
          isLoading:
            isLoading || _.includes([1, 2], getStaffGrades.networkStatus),
        };
      },
    }),
    withLoader
  )(WithToddleLibraryFilters);
};
export default ToddleLibraryFiltersHOC;
